import styles from './index.module.css'

import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuChevronUp } from '@react-icons/all-files/lu/LuChevronUp'
import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import React, { useEffect, useId, useMemo, useRef } from 'react'

import { TRACK } from '@disco/constants/ItemTypes'
import { useTranslation } from '@disco/i18n/use-translation'

import type { Shortlist } from 'types/shortlist'
import type { Track, TrackId } from 'types/track'

import TrackItem from 'components/common/track-item'
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Text,
} from 'components/ds'
import Draggable from 'components/ui/draggable'
import { HoverProvider } from 'components/ui/hover-provider'

import { SUGGESTIONS_CONTEXT } from './analytics'
import { SUGGESTED_TRACKS_COUNT } from './use-track-suggestions'

type Props = {
  shortlistId: Shortlist['id']
  tracks: Track[]
  loading: boolean
  error: boolean
  canRefresh: boolean
  /** Whether the playlist has a track that can seed a search. */
  seedReady: boolean
  /** The track the suggestions are similar to, when there is just one. */
  referenceName?: string
  collapsed: boolean
  /** Track being added with +, which blocks other adds until it finishes. */
  addingId: TrackId | null
  addError: boolean
  getDragData: (track: Track) => Record<string, unknown>
  onToggleCollapsed: () => void
  onRefresh: () => void
  onRetry: () => void
  onAdd: (track: Track, index: number) => void
  onDragStart: () => void
}

// True when the user moved focus elsewhere, as opposed to the pressed button
// losing it when it was disabled or removed.
const isFocusOutside = (panel: HTMLElement | null) => {
  const active = document.activeElement
  return Boolean(active && active !== document.body && !panel?.contains(active))
}

/** Lists suggested tracks for a playlist, to add with + or by dragging. */
const ShortlistSuggestions = ({
  shortlistId,
  tracks,
  loading,
  error,
  canRefresh,
  seedReady,
  referenceName,
  collapsed,
  addingId,
  addError,
  getDragData,
  onToggleCollapsed,
  onRefresh,
  onRetry,
  onAdd,
  onDragStart,
}: Props) => {
  const { t } = useTranslation('playlists')
  const headingId = useId()
  const contentId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const addButtons = useRef(new Map<TrackId, HTMLButtonElement>())
  // Row whose + was last pressed, so focus can return to that position.
  const focusAfterAdd = useRef<number | null>(null)
  const trackIds = useMemo(() => tracks.map(track => track.id), [tracks])

  useEffect(() => {
    const index = focusAfterAdd.current
    if (index === null || addingId !== null) return
    focusAfterAdd.current = null
    if (isFocusOutside(panelRef.current)) return
    const next = tracks[Math.min(index, tracks.length - 1)]
    const button = next && addButtons.current.get(next.id)
    ;(button ?? headingRef.current)?.focus()
  }, [addingId, tracks])

  let message: string | undefined
  // Also covers playlists whose audio is all outside the user's territories, as
  // those tracks can't seed a search.
  if (!seedReady)
    message = t(
      'playlists:addAudioTrackForSuggestions',
      'Add an audio track to see similar suggestions.',
    )
  else if (error)
    message = t(
      'playlists:couldNotLoadSuggestions',
      'Couldn’t load suggestions.',
    )
  else if (!loading && !tracks.length)
    message = canRefresh
      ? t(
          'playlists:noSuggestedTracksOnPage',
          'No suggestions on this page. Refresh to try more tracks.',
        )
      : t('playlists:noSuggestedTracks', 'No suggestions to show.')

  return (
    <section
      ref={panelRef}
      className={styles.suggestions}
      aria-labelledby={headingId}
    >
      <Flex
        align="center"
        justify="between"
        gap="1"
        wrap="wrap"
        p="3"
        pb={collapsed ? '3' : '0'}
      >
        <Heading ref={headingRef} id={headingId} as="h3" size="2" tabIndex={-1}>
          {t('playlists:suggestedTracks', 'Suggested tracks')}
        </Heading>
        <Flex align="center" gap="2">
          {!collapsed && seedReady && (
            <Button
              size="1"
              variant="soft"
              disabled={!canRefresh}
              onClick={onRefresh}
            >
              {t('playlists:refreshSuggestedTracks', 'Refresh')}
            </Button>
          )}
          <IconButton
            size="1"
            variant="ghost"
            aria-label={
              collapsed
                ? t(
                    'playlists:expandSuggestedTracks',
                    'Expand suggested tracks',
                  )
                : t(
                    'playlists:collapseSuggestedTracks',
                    'Collapse suggested tracks',
                  )
            }
            aria-expanded={!collapsed}
            aria-controls={contentId}
            onClick={onToggleCollapsed}
          >
            {collapsed ? (
              <LuChevronDown size={16} aria-hidden />
            ) : (
              <LuChevronUp size={16} aria-hidden />
            )}
          </IconButton>
        </Flex>
      </Flex>
      {/* .rt-Box sets display: block, which overrides hidden on its own. */}
      <Box
        id={contentId}
        hidden={collapsed}
        display={collapsed ? 'none' : undefined}
      >
        <Flex direction="column" gap="1" px="3" pt="1" pb="3">
          {seedReady && (
            <Text size="1" color="gray" className={styles.subtitle}>
              {referenceName
                ? t('playlists:similarToTrack', 'Similar to {{trackName}}', {
                    trackName: referenceName,
                  })
                : t(
                    'playlists:similarToTracksInThisPlaylist',
                    'Similar to tracks in this playlist',
                  )}
            </Text>
          )}
          {message && (
            <Text size="2" color="gray" role="status">
              {message}
            </Text>
          )}
          {error && (
            <Button size="1" variant="soft" onClick={onRetry}>
              {t('playlists:tryAgain', 'Try again')}
            </Button>
          )}
        </Flex>
        {loading && (
          <Flex
            direction="column"
            gap="2"
            px="3"
            pb="3"
            role="status"
            aria-label={t(
              'playlists:findingSimilarTracks',
              'Finding similar tracks',
            )}
            aria-busy="true"
          >
            {Array.from({ length: SUGGESTED_TRACKS_COUNT }, (_, index) => (
              <Skeleton key={index} height="48px" />
            ))}
          </Flex>
        )}
        {!loading && !error && tracks.length > 0 && (
          <ul className={styles.tracks}>
            {tracks.map((track, index) => (
              <li key={track.id}>
                <Flex align="center" pr="3" gap="2">
                  <Box flexGrow="1" minWidth="0">
                    <Draggable
                      id={`${shortlistId}-suggestion-track-${track.id}`}
                      type={TRACK}
                      data={getDragData(track)}
                      // Dropping it while + is importing it would import it twice.
                      disabled={addingId === track.id}
                      onDragStart={onDragStart}
                    >
                      <HoverProvider enableTouchSupport>
                        <TrackItem
                          track={track}
                          location="shortlist"
                          tracklist={trackIds}
                          playbackLocation={SUGGESTIONS_CONTEXT}
                        />
                      </HoverProvider>
                    </Draggable>
                  </Box>
                  <IconButton
                    ref={button => {
                      if (button) addButtons.current.set(track.id, button)
                      else addButtons.current.delete(track.id)
                    }}
                    size="1"
                    variant="soft"
                    aria-label={t(
                      'playlists:addTrackToPlaylist',
                      'Add {{trackName}} to playlist',
                      { trackName: track.name },
                    )}
                    disabled={addingId !== null}
                    loading={addingId === track.id}
                    onClick={() => {
                      focusAfterAdd.current = index
                      onAdd(track, index)
                    }}
                  >
                    <LuPlus size={16} aria-hidden />
                  </IconButton>
                </Flex>
              </li>
            ))}
          </ul>
        )}
        {addError && (
          <Text as="p" size="2" color="red" m="3" role="alert">
            {t(
              'playlists:couldNotAddTrack',
              'Couldn’t add this track. Please try again.',
            )}
          </Text>
        )}
      </Box>
    </section>
  )
}

export default ShortlistSuggestions
